import { createTheme, alpha } from '@mui/material/styles';

/**
 * ZPro Audio admin panel — central MUI theme.
 *
 * DARK GLASSMORPHIC look, matched to the zaudio app (D:\ztalk app/globals.css):
 * near-black aurora background, translucent "glass" surfaces with backdrop blur,
 * sky/blue/indigo brand (same as the apps) + amber accent. This is the single source of truth for
 * colour, type and component styling across the whole panel.
 *
 * The glass utilities (.glass, .aurora, .grid-veil, .text-gradient, animations)
 * live in src/index.css and are available to every page.
 */

const brand = '#2563eb';       // blue 600 — primary (ZPro Audio)
const brandDark = '#4f46e5';   // indigo 600
const brandBright = '#38bdf8'; // sky 400 — hover/active glow
const violet = '#7dd3fc';      // sky 300 — secondary accent
const amber = '#f59e0b';       // warm CTA accent

const BG = '#0b1120';          // app canvas (near-black)
const SURFACE = '#111a2e';     // solid surface base
const GLASS_BG = 'rgba(255,255,255,0.05)';
const GLASS_STRONG = 'rgba(17,26,46,0.88)';
const GLASS_BORDER = 'rgba(255,255,255,0.12)';
const TEXT = '#f1f5f9';
const MUTED = '#94a3b8';

export const SIDEBAR_WIDTH = 260;
export const palette = { brand, brandDark, brandBright, violet, amber, BG, SURFACE, GLASS_BG, GLASS_STRONG, GLASS_BORDER, TEXT, MUTED };

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: brand, dark: brandDark, light: brandBright, contrastText: '#ffffff' },
    secondary: { main: violet, contrastText: '#1e1b4b' },
    success: { main: '#34d399' },
    warning: { main: amber },
    error: { main: '#f87171' },
    info: { main: brandBright },
    background: { default: BG, paper: SURFACE },
    text: { primary: TEXT, secondary: MUTED, disabled: 'rgba(148,163,184,0.5)' },
    divider: GLASS_BORDER,
  },

  shape: { borderRadius: 14 },

  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 800, fontSize: '2rem', letterSpacing: '-0.02em' },
    h2: { fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, fontSize: '1.35rem', letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, fontSize: '1.15rem' },
    h5: { fontWeight: 600, fontSize: '1.05rem' },
    h6: { fontWeight: 600, fontSize: '0.95rem' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, color: MUTED },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: BG, color: TEXT },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          background: GLASS_BG,
          border: `1px solid ${GLASS_BORDER}`,
          borderRadius: 18,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
          color: TEXT,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 11, paddingInline: 18, paddingBlock: 9 },
        containedPrimary: {
          background: `linear-gradient(135deg, ${brand}, ${brandDark})`,
          boxShadow: `0 6px 18px ${alpha(brand, 0.45)}`,
          '&:hover': { background: `linear-gradient(135deg, ${brandBright}, ${brand})` },
        },
        outlined: {
          borderColor: GLASS_BORDER,
          color: TEXT,
          '&:hover': { borderColor: brandBright, backgroundColor: alpha(brandBright, 0.08) },
        },
        textInherit: { color: MUTED },
      },
    },

    MuiTextField: { defaultProps: { size: 'small', fullWidth: true, variant: 'outlined' } },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 11,
          backgroundColor: 'rgba(255,255,255,0.04)',
          color: TEXT,
          '& fieldset': { borderColor: GLASS_BORDER },
          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
          '&.Mui-focused fieldset': { borderColor: brandBright },
        },
        input: {
          '&::placeholder': { color: MUTED, opacity: 1 },
          // Chrome's saved-login fill paints a solid light-blue box; keep our dark glass look instead.
          // Chrome paints saved-login fills with its own light-blue background (and newer Chrome
          // ignores the old transition trick), so cover it with an inset shadow in our field colour.
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active, &:autofill': {
            WebkitBoxShadow: '0 0 0 1000px #1e2838 inset',
            boxShadow: '0 0 0 1000px #1e2838 inset',
            WebkitTextFillColor: TEXT,
            caretColor: TEXT,
            transition: 'background-color 99999s ease-in-out 0s',
            borderRadius: 0,
          },
        },
      },
    },

    MuiInputLabel: { styleOverrides: { root: { color: MUTED, fontWeight: 500 } } },
    MuiFormHelperText: { styleOverrides: { root: { color: MUTED } } },

    MuiMenu: { styleOverrides: { paper: { backgroundColor: GLASS_STRONG, border: `1px solid ${GLASS_BORDER}`, backdropFilter: 'blur(20px)', color: TEXT } } },
    MuiPopover: { styleOverrides: { paper: { backgroundColor: GLASS_STRONG, border: `1px solid ${GLASS_BORDER}`, backdropFilter: 'blur(20px)' } } },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 8 },
        outlined: { borderColor: GLASS_BORDER },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: 'rgba(2,6,23,0.95)', border: `1px solid ${GLASS_BORDER}`, fontSize: 12 },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: GLASS_STRONG, border: `1px solid ${GLASS_BORDER}`, backdropFilter: 'blur(20px)', backgroundImage: 'none' },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: GLASS_STRONG,
          backdropFilter: 'saturate(160%) blur(16px)',
          WebkitBackdropFilter: 'saturate(160%) blur(16px)',
          borderBottom: `1px solid ${GLASS_BORDER}`,
          color: TEXT,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${GLASS_BORDER}`,
          backgroundColor: GLASS_STRONG,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          color: '#cbd5e1',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 11,
          marginBottom: 2,
          color: '#cbd5e1',
          '& .MuiListItemIcon-root': { color: '#94a3b8', minWidth: 38 },
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#fff' },
          '&.Mui-selected': {
            background: `linear-gradient(135deg, ${alpha(brand, 0.9)}, ${alpha(brandDark, 0.9)})`,
            color: '#fff',
            '& .MuiListItemIcon-root': { color: '#fff' },
            '&:hover': { background: `linear-gradient(135deg, ${brand}, ${brandDark})` },
          },
        },
      },
    },

    MuiTableCell: { styleOverrides: { root: { borderColor: GLASS_BORDER, color: TEXT } } },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${GLASS_BORDER}`,
          borderRadius: 18,
          background: GLASS_BG,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          color: TEXT,
          '--DataGrid-rowBorderColor': 'rgba(255,255,255,0.07)',
          '--DataGrid-containerBackground': 'transparent',
          fontSize: '0.875rem',
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
          '& .MuiTablePagination-root, & .MuiDataGrid-menuIconButton, & .MuiDataGrid-sortIcon, & .MuiDataGrid-filterIcon': { color: MUTED },
        },
        columnHeaders: { borderBottom: `1px solid ${GLASS_BORDER}` },
        columnHeader: { backgroundColor: 'rgba(255,255,255,0.03)', color: MUTED },
        columnHeaderTitle: { fontWeight: 700 },
        cell: { borderColor: 'rgba(255,255,255,0.07)' },
        row: { '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' } },
        footerContainer: { borderTop: `1px solid ${GLASS_BORDER}` },
        overlay: { backgroundColor: 'transparent', color: MUTED },
      },
    },
  },
});

export default theme;
